import * as React from "react";
import { Button, Card, Image, makeStyles, tokens } from "@fluentui/react-components";
import useIsAuthenticated from "../services/authentication/useIsAuthenticated";
import { Navigate } from "react-router-dom";

const SignInForm = React.lazy(() => import("../components/forms/SignInForm"));
export interface SignInProps {
  title: string;
  logo: string;
  message: string;
}

const useStyles = makeStyles({
  header: {
    alignItems: "center",
    paddingTop: "10px",
    paddingBottom: "10px",
  },
  header__h1: {
    fontFamily: "Jost",
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightMedium,
    color: tokens.colorNeutralForeground4,
  },
  signin_card: {
    marginTop: "20px",
    marginBottom: "20px",
    paddingTop: "20px",
    paddingBottom: "80px",
  },
  signin__text: {
    textAlign: "center",
    marginTop: "20px",
    marginBottom: "30px",
    color: tokens.colorNeutralForeground4,
    fontWeight: tokens.fontWeightMedium,
  },
  signin__p: {
    textAlign: "center",
  },
  signin_google: {
    marginTop: "5px",
    marginLeft: "10px",
    marginRight: "10px",
  },
  common: {
    marginTop: "20px",
    alignItems: "center",
    fontSize: "medium",
  },
  common_p: {
    textAlign: "center",
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground4,
    fontWeight: tokens.fontWeightMedium,
  },
  footer: {
    alignItems: "center",
    marginTop: "15px",
  },
});

/* This code defines a functional component named `SignIn` which takes in props of type `SignInProps`.
Inside the component, it destructures the `title`, `logo`, and `message` from the props. */
const SignIn: React.FC<SignInProps> = (props: SignInProps) => {
  const { title, logo, message } = props;
  const styles = useStyles();

  // Auth
  const isAuthenticated: boolean = useIsAuthenticated();

  if (isAuthenticated) return <Navigate to="dashboard" replace={true} />;

  return (
    <>
      <Card orientation="vertical" className={styles.header}>
        <Image src={logo} alt={title} shape="rounded" fit="contain" style={{ width: "80%" }} />
        <h1 className={styles.header__h1}>{message}</h1>
        <p style={{ textAlign: "center", color: "#858585", fontSize: tokens.fontSizeBase200 }}>
          Easily add QR Codes to your documents for instant verification. Recipients can scan the code with their phone
          to access a secure landing page showing the original document details.{" "}
          <a
            href="https://support.qrmark.com/hc/en-us/articles/36675956351385-Integrate-With-Microsoft-AppSource-App"
            target="_blank"
            rel="noreferrer"
            style={{ textDecoration: "underline", color: "#858585" }}
          >
            Learn more
          </a>
        </p>
      </Card>

      <Card orientation="vertical" className={styles.signin_card}>
        <section style={{ textAlign: "center" }}>
          <p className={styles.signin__text}>Sign in to your account</p>
          <SignInForm />
        </section>
        <p className={styles.signin__p}></p>
        <div style={{ borderBottom: "1px solid #C8C4C3", textAlign: "center", lineHeight: "0.1em", margin: "10px" }}>
          <span style={{ backgroundColor: "white", padding: "0 10px" }}>or</span>
        </div>

        {/* Depricated: Google OAuth is not supported in the Microsoft Add-in Desktop environment. */}
        <section className={styles.signin_google}>
          {/* <GoogleOAuthProvider clientId={`${process.env.REACT_APP_GOOGLE_OAUTH_KEY}`}>
            <ContinueWithGoogle />
          </GoogleOAuthProvider> */}
          <Button
            appearance="outline"
            size="medium"
            style={{ width: "100%", color: "gray" }}
            onClick={() =>
              // eslint-disable-next-line no-undef
              window.open(
                "https://app.qrmark.com/signup?utm_source=microsoft&utm_medium=ms-addin&utm_campaign=sign-in",
                "_blank"
              )
            }
          >
            Create Account
          </Button>
          <p style={{ fontSize: tokens.fontSizeBase200, color: "gray" }}>
            Note: Sign up with your email and password to use the QR Mark Microsoft Add-in
          </p>
        </section>
      </Card>

      <Card className={styles.common}>
        <p className={styles.common_p}>
          By signing up you agree to our
          <a
            href="https://qrmark.com?utm_source=microsoft&utm_medium=ms-addin&utm_campaign=sign-in"
            target="_blank"
            rel="noreferrer"
            style={{ paddingLeft: "4px", paddingRight: "4px", fontSize: tokens.fontSizeBase200, color: "gray" }}
          >
            Terms of Service
          </a>
          &
          <a
            href="https://qrmark.com/how-to-use?utm_source=microsoft&utm_medium=ms-addin&utm_campaign=sign-in"
            target="_blank"
            rel="noreferrer"
            style={{ paddingRight: "4px", paddingLeft: "4px", fontSize: tokens.fontSizeBase200, color: "gray" }}
          >
            Privacy Policy.
          </a>
        </p>
        <div style={{ display: "flex", justifyContent: "center", marginBottom: "10px" }}>
          <a
            href="https://qrmark.com?utm_source=microsoft&utm_medium=ms-addin&utm_campaign=sign-in"
            target="_blank"
            rel="noreferrer"
            style={{ fontSize: tokens.fontSizeBase200, color: "gray", textDecoration: "none" }}
          >
            Home
          </a>
          <span style={{ margin: "0 8px", color: "gray" }}>|</span>
          <a
            href="https://qrmark.com/support?utm_source=microsoft&utm_medium=ms-addin&utm_campaign=sign-in"
            target="_blank"
            rel="noreferrer"
            style={{ fontSize: "small", color: "gray", textDecoration: "none" }}
          >
            Support
          </a>
          <span style={{ margin: "0 8px", color: "gray" }}>|</span>
          <a
            href="https://qrmark.com/pricing?utm_source=microsoft&utm_medium=ms-addin&utm_campaign=sign-in"
            target="_blank"
            rel="noreferrer"
            style={{ fontSize: "small", color: "gray", textDecoration: "none" }}
          >
            Pricing
          </a>
        </div>
      </Card>
    </>
  );
};

export default SignIn;
